import { OrganizationSearchResults } from '@/components/organization-search-results';
import { PartnerSearchResults } from '@/components/partner-search-results';
import SearchInput, { SearchInputRef } from '@/components/searchInput';
import { useDebouncedSearchInput } from '@/hooks/useDebouncedSearchInput';
import { useOrganizationSearchQuery } from '@/hooks/useOrganizationSearchQuery';
import { usePartnerSearchQuery } from '@/hooks/usePartnerSearchQuery';
import useOrg from '@/hooks/useOrg';
import usePartner from '@/hooks/usePartner';
import { Organization } from '@/types/organizations';
import { Partner } from '@/types/partners';
import { Box, Flex, Separator, Tag } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

interface OrganizationAndPartnerSearchProps {
  onOrgSelect?: (org: Organization) => void;
  placeholder?: string;
}

export const OrganizationAndPartnerSearch = ({
  onOrgSelect,
  placeholder = 'Search',
}: OrganizationAndPartnerSearchProps) => {
  const org = useOrg();
  const partner = usePartner();
  const navigate = useNavigate();
  const searchInputRef = useRef<SearchInputRef>(null);
  const queryClient = useQueryClient();

  const { searchQuery, debouncedSearchQuery, handleQueryChange, isOpen, setIsOpen, clearSearch, isDebouncing } =
    useDebouncedSearchInput();
  const { organizations, isLoading: isOrgsLoading } = useOrganizationSearchQuery(debouncedSearchQuery);
  const { partners, isLoading: isPartnersLoading } = usePartnerSearchQuery(debouncedSearchQuery);

  useEffect(() => {
    clearSearch();
    searchInputRef.current?.resetQuery();
  }, [org?.name, partner?.name, clearSearch]);

  const handleOrgSelect = (selectedOrg: Organization) => {
    clearSearch();
    searchInputRef.current?.resetQuery();

    queryClient.invalidateQueries({
      queryKey: ['data-table'],
      exact: false,
    });

    onOrgSelect?.(selectedOrg);
    navigate(`/organization/${selectedOrg.id}`);
  };

  const handlePartnerSelect = (partner: Partner) => {
    clearSearch();
    searchInputRef.current?.resetQuery();
    navigate(`/partners/${partner.id}`);
  };

  const handleInputFocus = () => {
    if (searchQuery.length > 0) {
      setIsOpen(true);
    }
  };

  const handleInputBlur = () => {
    setTimeout(() => setIsOpen(false), 200);
  };

  const selectedName = org?.name || partner?.name || null;
  const isOrgResultsLoading = isDebouncing || isOrgsLoading;
  const isPartnerResultsLoading = isDebouncing || isPartnersLoading;

  return (
    <Box position="relative" width="100%">
      <Flex width="100%" direction="column" gap={1}>
        <Box onFocus={handleInputFocus} onBlur={handleInputBlur} bg="white">
          <SearchInput
            ref={searchInputRef}
            placeholder={placeholder}
            query={searchQuery}
            setQuery={handleQueryChange}
            styles={{ width: '100%' }}
          />
        </Box>
        <span>
          {selectedName && (
            <Tag.Root color={'secondary'}>
              <Tag.Label>{selectedName}</Tag.Label>
              <Tag.EndElement>
                <Tag.CloseTrigger onClick={() => navigate('/')} />
              </Tag.EndElement>
            </Tag.Root>
          )}
        </span>
      </Flex>

      {isOpen && (
        <Box
          position="absolute"
          top="100%"
          left={0}
          right={0}
          bg="white"
          border="1px solid"
          borderColor="gray.300"
          borderRadius="md"
          boxShadow="lg"
          zIndex={1000}
          mt={1}
        >
          <OrganizationSearchResults
            organizations={organizations}
            isLoading={isOrgResultsLoading}
            searchQuery={searchQuery}
            onOrgSelect={handleOrgSelect}
          />
          <Separator />
          {!isPartnerResultsLoading && (
            <PartnerSearchResults partners={partners} searchQuery={searchQuery} onPartnerSelect={handlePartnerSelect} />
          )}
        </Box>
      )}
    </Box>
  );
};
